import { useUser } from "@/zustand/store";
import { ArrowLeft, LogOut, MessageCircleMore } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { useSheetStore } from "@/zustand/sheetStore";
import { useModalStore } from "@/zustand/modalStore";

const TopBar = ({
  title,
  className,
  backTo,
}: {
  title: string;
  className?: string;
  backTo?: string;
}) => {
  const [optionIsOpen, setOptionIsOpen] = useState(false);
  const { user } = useUser((state: any) => state);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);
  const { openSheet } = useSheetStore();
  const { openModal } = useModalStore();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        setOptionIsOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOptionIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  return (
    <>
      <nav
        className={`font-poppins bg-dark-card-3 border-dark-stroke-3 sticky inset-x-0 top-0 z-20 flex w-full max-w-screen items-center justify-between border-b px-4 py-4 text-white lg:px-6 ${className}`}
      >
        <div className="flex items-center gap-2">
          {backTo && (
            <Button
              onClick={() => navigate(backTo)}
              className="text-dark-primary-400 size-8"
              variant={"ghost"}
            >
              <ArrowLeft />
            </Button>
          )}
          <p className="text-dark-primary-400 text-lg leading-3 font-semibold lg:text-2xl">
            {title}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => openModal("contactUs")}
            className="bg-dark-primary-50 hover:bg-dark-primary-100 flex size-8 items-center justify-center rounded-full text-white hover:cursor-pointer active:scale-95 md:size-9 lg:hidden"
          >
            <MessageCircleMore className="text-dark-primary-400 size-4.5 md:size-6" />
          </button>
          <button
            onClick={() => openSheet("contactUs")}
            className="bg-dark-primary-50 hover:bg-dark-primary-100 hidden size-8 items-center justify-center rounded-full text-white hover:cursor-pointer active:scale-95 md:size-9 lg:flex"
          >
            <MessageCircleMore className="text-dark-primary-400 size-4.5 md:size-6" />
          </button>

          <div className="relative">
            <div
              ref={toggleRef}
              onClick={() => setOptionIsOpen(!optionIsOpen)}
              role="button"
              aria-label="Toggle user menu"
              aria-expanded={optionIsOpen}
              className="text-dark-primary-400 bg-dark-primary-50 hover:bg-dark-primary-100 flex size-8 cursor-pointer items-center justify-center rounded-full border border-white/20 transition-all active:scale-90 md:size-9"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full text-xl font-medium uppercase lg:text-2xl">
                {user?.firstName?.charAt(0)}
              </span>
            </div>

            {optionIsOpen && (
              <div className="options-menu absolute top-[calc(100%+20px)] right-0 z-20 w-60 overflow-hidden rounded-xl border bg-white px-4 pt-4">
                <div
                  ref={menuRef}
                  role="menu"
                  className="flex items-center gap-2"
                >
                  <span className="bg-primary-500 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white">
                    {user?.firstName?.charAt(0)}
                  </span>
                  <p className="text-primary-500 text-sm">
                    {user?.firstName} {user?.lastName}
                  </p>
                </div>
                <p className="text-primary-500/80 mt-2 text-xs">
                  {user?.email}
                </p>

                <div className="bg-primary-bg my-2 h-[1px]" />

                <div
                  onClick={() => {
                    useUser.setState({ user: undefined });
                    localStorage.removeItem("token");
                    toast.success("Logout successful");
                    navigate("/login");
                  }}
                  className="text-primary-500 hover:bg-primary-500/10 -mx-4 flex items-center gap-2 px-4 py-2 text-base font-medium"
                >
                  <span>Logout</span> <LogOut className="size-4" />
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default TopBar;
